import { dateFilterParams, formatLongPrice, formatPercentage, headerInfoTooltipDef } from '../lib/utils';
import parseISO from 'date-fns/parseISO';
import formatDate from 'date-fns/format';
import { ColDef, ColGroupDef } from '@ag-grid-community/core';
import React from 'react';

const defaultFilterParams = {
  newRowsAction: 'keep',
};

export default [
  {
    cellClass: 'ticker-cell',
    cellRenderer: 'tickerWidgetCell',
    field: 'ticker',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    initialWidth: 50,
    keyCreator: function (params) {
      const tickers: string[] = [];
      params.value.forEach((s: any) => {
        s.forEach((el: any) => {
          tickers.push(el.symbol);
        });
      });
      return tickers;
    },
    valueGetter: function ({ data }: { data: any }) {
      if (!data.companies || data.companies.length === 0) {
        return [];
      }
      const securities: any[] = [];
      data.companies.forEach((c: any) => {
        securities.push(...c.securities);
      });
      if (securities.length === 0) {
        return [];
      }

      const tickers = Array.from(new Set(securities.map((item: any) => item.symbol)));
      return tickers;
    },
    ...headerInfoTooltipDef('Unique set of symbols/characters assigned to a company on an exchange'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: ({ data }: { data: any }) => {
      if (!data.companies || data.companies.length === 0) {
        return '-';
      }
      return (
        <>
          {data.companies.map((company: any, index: number) => {
            const logo = company.securities[0]?.logo;
            const ticker = company.securities[0]?.symbol;

            if (company.securities && company.securities.length > 0) {
              return (
                <React.Fragment key={`${company.symbol}-${index}`}>
                  <span>{company.name}</span>
                  {logo && ticker && <img alt={ticker} className="bz-ag-table__logo" height={17} src={logo} />}
                  <br />
                </React.Fragment>
              );
            } else {
              return null;
            }
          })}
        </>
      );
    },
    field: 'name',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Company',
    initialWidth: 120,
    keyCreator: function (params) {
      return params.value.map(function (company: any) {
        return company.name;
      });
    },
    valueGetter: function (params) {
      return params.data['companies'];
    },
    ...headerInfoTooltipDef('Name of company developing drug being reported on'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'quote.last',
    headerName: 'Price',
    initialWidth: 60,
    suppressMenu: true,
    valueFormatter: ({ data }: any) => formatLongPrice(data?.quote?.last),
    ...headerInfoTooltipDef('Current Stock Price'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'quote.changePercent',
    headerName: '% change',
    initialWidth: 60,
    suppressMenu: true,
    valueFormatter: ({ data }: any) => formatPercentage(data?.quote?.changePercent),
    ...headerInfoTooltipDef('% Change in Price from Open'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: ({ data }: { data: any }) => {
      if (!data.drug && !data?.drug?.name) return '';
      return (
        <>
          <b>{data.drug.name}</b>
          <br />
          <span style={{ fontWeight: 'normal' }}>{data.drug.indication_symptom.join(', ')}</span>
        </>
      );
    },
    field: 'drug',
    headerName: 'Name and Treatment',
    initialWidth: 130,
    suppressMenu: true,
    ...headerInfoTooltipDef('Drug name and symptoms/diseases it is used to treat'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'status',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Status',
    initialWidth: 70,
    ...headerInfoTooltipDef('Current status in testing'),
  },
  {
    cellClass: 'ticker-cell',
    comparator: (valueA, valueB) => {
      // "target_date": "string (YYYY-MM, YYYY-Q[1-4], YYYY-H[1-2], or YYYY-[EARLY, MID, LATE])",
      const sortBy = [
        '01',
        '02',
        '03',
        'EARLY',
        'Q1',
        '04',
        '05',
        '06',
        'MID',
        'Q2',
        'H1',
        '07',
        '08',
        '09',
        'Q3',
        '10',
        '11',
        '12',
        'LATE',
        'Q4',
        'H2',
      ];

      try {
        const formattedDateA = valueA.toString().split('-');
        const formattedDateB = valueB.toString().split('-');

        const periodA = formattedDateA;
        const periodB = formattedDateB;
        let [yearA] = formattedDateA;
        let [yearB] = formattedDateB;

        yearA = parseInt(yearA);
        yearB = parseInt(yearB);

        if (isNaN(yearA) || isNaN(yearB)) return 0;

        if (yearA > yearB) {
          return 1;
        } else if (yearA < yearB) {
          return -1;
        } else {
          const periodAIndex = sortBy.indexOf(periodA.toString());
          const periodBIndex = sortBy.indexOf(periodB.toString());
          if (periodAIndex === periodBIndex) return 0;
          return periodAIndex > periodBIndex ? 1 : -1;
        }
      } catch (error) {
        console.error('estimated_date comparator err', error);
        return 0;
      }
    },
    field: 'estimated_date',
    filter: 'agSetColumnFilter',

    // initialSort: 'asc',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
    },

    headerName: 'Catalyst Date',
    initialWidth: 100,
    valueGetter: ({ data }) => {
      if (!data.target_date) return '';
      return data.target_date;
    },
    ...headerInfoTooltipDef('Catalyst Date'),
  },
  {
    cellClass: 'ticker-cell',
    field: 'exact_date',
    filter: 'agDateColumnFilter',
    // initialSort: 'asc',
    filterParams: {
      ...defaultFilterParams,
      ...dateFilterParams,
      buttons: ['clear'],
      inRangeInclusive: true,
    },

    headerName: 'Catalyst Date',
    initialWidth: 100,
    valueFormatter: ({ data }) => data.target_date,
    valueGetter: ({ data }) => {
      if (!data.target_date) return null;

      return data.target_date;
    },
    ...headerInfoTooltipDef('Catalyst Date'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: ({ value }: any) => {
      if (!value) return '';
      return (
        <>
          {formatDate(value, 'MM/dd/yyyy')} <br /> {formatDate(value, 'h:mm a')}
        </>
      );
    },
    field: 'historical_date',
    filter: 'agDateColumnFilter',
    headerName: 'Updated',
    initialSort: 'desc' as ColDef['initialSort'],
    initialWidth: 70,
    valueGetter: ({ data }) => {
      if (!data.time || !data.date) return '';
      const date_time = `${data.date}T${data.time}`;
      return parseISO(date_time);
    },
    ...headerInfoTooltipDef('Date and time of announcement from FDA'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    field: 'event_type',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Catalyst',
    initialWidth: 70,
    ...headerInfoTooltipDef('Catalyst of event being reported'),
  },
  {
    autoHeight: true,
    cellClass: 'wrap-cell',
    cellRenderer: ({ data }: { data: any }) => {
      if (!data.outcome) return '';
      return (
        <>
          <p style={{ fontWeight: 'normal' }}>{data.outcome}</p>
          {data.source_type && data.source_link && (
            <a href={data.source_link} rel="noreferrer" target="_blank">
              View
            </a>
          )}
        </>
      );
    },
    field: 'outcome',
    filter: 'agSetColumnFilter',
    filterParams: {
      ...defaultFilterParams,
      buttons: ['clear'],
      defaultToNothingSelected: true,
    },
    headerName: 'Description',
    initialWidth: 200,
    suppressMenu: true,
    ...headerInfoTooltipDef('Description of Event'),
  },
  {
    cellClass: 'ticker-cell',
    cellRenderer: () => {
      return (
        // eslint-disable-next-line react/jsx-no-target-blank
        <a href="https://www.benzinga.com/profile/portfolio/?action=login&utm_source=calendars" target="_blank">
          Get Alert
        </a>
      );
    },
    field: 'get_alert',
    headerName: 'Get Alert',
    initialWidth: 70,
    suppressAutoSize: true,
    suppressMenu: true,
    suppressSizeToFit: true,
  },
] as (ColDef | ColGroupDef)[];
